import { createContext, Fragment, useContext } from "react";
import { Dialog as HeadlessDialog, Transition } from "@headlessui/react";
import { cn } from "@/lib/utils";

const DialogCloseContext = createContext<(() => void) | null>(null);

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

function Dialog({ open, onOpenChange, children }: DialogProps) {
  const close = () => onOpenChange(false);
  return (
    <DialogCloseContext.Provider value={close}>
      <Transition show={open} as={Fragment}>
        <HeadlessDialog
          as="div"
          className="relative z-50"
          onClose={close}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {children}
        </HeadlessDialog>
      </Transition>
    </DialogCloseContext.Provider>
  );
}

interface DialogContentProps {
  className?: string;
  children: React.ReactNode;
}

function DialogContent({ className, children }: DialogContentProps) {
  const onClose = useContext(DialogCloseContext);

  return (
    <>
      <div
        data-fluxi-nested-modal
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose ?? undefined}
      />
      <div
        data-fluxi-nested-modal
        className="fixed inset-0 overflow-y-auto pointer-events-none"
      >
        <div className="flex min-h-full items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <HeadlessDialog.Panel
              className={cn("w-full max-w-full pointer-events-auto", className)}
            >
              {children}
            </HeadlessDialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </>
  );
}

export { Dialog, DialogContent };
